import React, { useEffect, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Lock, Fingerprint, Delete, Loader2 } from "lucide-react";
import { CircuitBg } from "@/components/CircuitBg";
import { GlassButton } from "@/components/GlassBits";
import { unlockWithBiometric } from "@/lib/biometric";
import { stormSurge, stormPulse } from "@/lib/pulse";

const UNLOCK_KEY = "app_unlocked";

/** Pantalla de bloqueo: pide PIN (o huella) antes de abrir el panel. */
export const AppLock = ({ children }) => {
  const [state, setState] = useState("checking");
  const [lock, setLock] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    userApi.get("/user/security")
      .then(({ data }) => {
        setLock(data.app_lock);
        const unlocked = sessionStorage.getItem(UNLOCK_KEY) === "1";
        setState(data.app_lock.enabled && !unlocked ? "locked" : "open");
      })
      .catch(() => setState("open"));
  }, []);

  const open = () => {
    sessionStorage.setItem(UNLOCK_KEY, "1");
    stormSurge();
    setState("open");
  };

  const submit = async (value) => {
    setBusy(true);
    try {
      await userApi.post("/user/security/pin/verify", { pin: value });
      open();
    } catch (e) {
      toast.error(apiError(e, "PIN incorrecto"));
      setPin("");
    } finally { setBusy(false); }
  };

  const press = (digit) => {
    stormPulse();
    const next = (pin + digit).slice(0, 8);
    setPin(next);
    if (next.length === 4) submit(next);
  };

  const biometric = async () => {
    try {
      await unlockWithBiometric(lock?.credential_id);
      open();
    } catch {
      toast.error("No se pudo verificar tu huella. Usa tu PIN.");
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center circuit-bg theme-noir" style={{ backgroundColor: "#05070C" }}>
        <CircuitBg />
      <div className="circuit-veil" aria-hidden="true" />
        <Loader2 className="w-7 h-7 animate-spin text-red-400 relative z-10" />
      </div>
    );
  }
  if (state === "open") return children;

  return (
    <div className="min-h-screen flex items-center justify-center circuit-bg theme-noir" style={{ backgroundColor: "#05070C" }}>
      <CircuitBg />
      <div className="circuit-veil" aria-hidden="true" />
      <div className="w-full max-w-xs px-6 relative z-10" data-testid="app-lock">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
            style={{ background: "linear-gradient(135deg,#FF8A2B,#3A1703)", boxShadow: "0 0 38px rgba(224,16,32,0.45)" }}>
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-heading text-xl font-bold text-white">App bloqueada</h1>
          <p className="text-xs text-white/55 mt-1">Ingresa tu PIN para continuar</p>
        </div>

        <div className="glass-card p-5">
          <div className="flex justify-center gap-2.5 mb-5" data-testid="pin-dots">
            {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
              <span key={`pin-dot-${i}`} className={`w-3 h-3 rounded-full border transition-colors ${
                i < pin.length ? "bg-red-500 border-red-400" : "border-white/25"
              }`} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button key={n} data-testid={`pin-key-${n}`} onClick={() => press(String(n))} disabled={busy}
                className="glass-btn h-12 rounded-xl text-lg font-semibold">{n}</button>
            ))}
            <button data-testid="pin-backspace" onClick={() => setPin((p) => p.slice(0, -1))}
              className="glass-btn h-12 rounded-xl flex items-center justify-center"><Delete className="w-4 h-4" /></button>
            <button data-testid="pin-key-0" onClick={() => press("0")} disabled={busy}
              className="glass-btn h-12 rounded-xl text-lg font-semibold">0</button>
            <button data-testid="pin-submit" onClick={() => pin.length >= 4 && submit(pin)} disabled={busy}
              className="glass-btn glass-btn-red h-12 rounded-xl flex items-center justify-center">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "OK"}
            </button>
          </div>

          {lock?.biometric && (
            <GlassButton tone="ghost" icon={Fingerprint} className="mt-4" data-testid="pin-biometric"
              onClick={biometric}>Usar huella o Face ID</GlassButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppLock;
